import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { I18nProvider } from "./i18n";
// 174 §1.2 — the boot identity seed runs before api() is wired, so it keeps its
// bare fetch and sends the same three headers api() sends.
import { authHeaders } from "./api";

// Polyfill window.storage to use localStorage for the standalone app
// This mimics the Claude.ai persistent storage API
window.storage = {
  get(key: any) {
    return new Promise((resolve, reject) => {
      try {
        const value = localStorage.getItem(key);
        if (value !== null) {
          resolve({ key, value });
        } else {
          // Match Claude's behavior: throw when key doesn't exist
          reject(new Error("Key not found: " + key));
        }
      } catch (e) {
        reject(e);
      }
    });
  },
  set(key: any, value: any) {
    return new Promise((resolve, reject) => {
      try {
        localStorage.setItem(key, value);
        resolve({ key, value });
      } catch (e) {
        reject(e);
      }
    });
  },
  delete(key: any) {
    return new Promise((resolve) => {
      localStorage.removeItem(key);
      resolve({ key, deleted: true });
    });
  },
  list(prefix = "") {
    return new Promise((resolve) => {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k!.startsWith(prefix)) keys.push(k);
      }
      resolve({ keys });
    });
  },
};

// 167 §2 — THE SESSION IS A COOKIE, NEVER THE URL. Microsoft sign-in used to
// return to /?lxAuth=<jwt> and this parsed the token out; now the callback sets
// the httpOnly lx_session cookie and redirects to `/` bare, so there is nothing
// to parse and no token to store.
//
// What the M365 return DOES need is the identity: it lands with a live cookie
// and no stored lx:authUser. So when the browser holds no stored identity, ask
// the server who it is under the cookie (GET /api/me) BEFORE mounting, and seed
// the two identity slots the app reads — the same values the URL parse wrote,
// read from the server instead. This fires ONLY when identity is missing: a
// returning user with lx:authUser already in storage skips it (one request, and
// only on the boot that needs it), and a never-signed-in visitor gets a 401 and
// simply renders the login screen. No URL marker, no server redirect change —
// one unconditional /api/me when, and only when, there is a cookie but no name.
async function seedIdentityFromSessionIfNeeded() {
  try {
    if (localStorage.getItem("lx:authUser")) return; // already known
    // A short timeout so a slow or hung /api/me still renders the login screen
    // rather than a white boot; a real session recovers on the next call.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    let r;
    try {
      // 174 §1.2 — BOTH credentials. This ran cookie-only, so a returning
      // user holding the lx:token Bearer and no cookie got a 401 here, the
      // seed returned early, and the app took its identity from lx:authUser
      // in storage — a console showing a resolved authUserId while /api/me
      // had never successfully answered.
      r = await fetch("/api/me", {
        credentials: "same-origin",
        headers: authHeaders(),
        signal: ctrl.signal,
      });
    } finally {
      clearTimeout(timer);
    }
    if (!r.ok) return; // no cookie (401) → the app renders the login screen
    const u = await r.json();
    if (u && u.id != null) {
      const identity = JSON.stringify({
        id: u.id,
        email: u.email || "",
        role: u.role || "",
        isAdmin: !!u.isAdmin,
      });
      localStorage.setItem("lx:authUser", identity);   // the switcher-gate slot
      localStorage.setItem("lx:currentUser", identity); // the display slot
    }
  } catch {
    /* a boot that cannot reach /api/me renders the login screen; a real
       session recovers on the next successful call */
  }
}

// PWA: capture install prompt for the in-app "Install App" button, and
// register a minimal service worker so Chrome marks the site installable.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Phase 30 (F / D8) — dead-tab auto-reload. A long-lived tab whose dynamic
// chunk import 404s (the deploy replaced the hashed files) is already broken;
// Vite surfaces that as `vite:preloadError`. Reload at most once per minute
// so a genuinely-offline client can't reload-loop.
window.addEventListener("vite:preloadError", () => {
  try {
    const last = Number(sessionStorage.getItem("lx:chunkReload") || 0);
    if (Date.now() - last < 60000) return;
    sessionStorage.setItem("lx:chunkReload", String(Date.now()));
  } catch {}
  window.location.reload();
});
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__lxInstallPrompt = e;
  window.dispatchEvent(new CustomEvent("lx:pwa-installable"));
});
window.addEventListener("appinstalled", () => {
  window.__lxInstallPrompt = null;
  window.dispatchEvent(new CustomEvent("lx:pwa-installed"));
});

seedIdentityFromSessionIfNeeded().finally(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <I18nProvider>
        <App />
      </I18nProvider>
    </React.StrictMode>,
  );
});
